import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import SearchInput from '../SearchInput/SearchInput';
import QuickSearch from '../QuickSearch/QuickSearch';
import { useSearchParam } from '../../hooks/useSearchParam';
import { useIsDesktopLayout } from '../../hooks/useIsDesktopLayout';
import { isNativeSelector } from '../../redux/reducers/app/params';
import { getDataTestAttrs } from '../../utils/testing-locators';
import {
  SEARCH_QUERY_PARAM,
  MIN_COUNT_CHARACTERS_FOR_SEARCH,
} from '../../constants/search';
import { SEARCH } from '../../constants/routeTemplates';
import { SEARCH_FORM } from '../../constants/dataTestIds';
import { useAutoFocus, useSearchQuery } from './hooks';
import styles from './SearchBar.module.css';

const SearchBar = ({ className }) => {
  const isDesktop = useIsDesktopLayout();
  const isNative = useSelector(isNativeSelector);

  const history = useHistory();
  const inputRef = useAutoFocus();
  const { pathname } = useLocation();

  const query = useSearchParam(SEARCH_QUERY_PARAM);
  const [searchInputValue, setSearchInputValue] = useState(query);
  const { searchQuery, setSearchValue } = useSearchQuery(query);
  const [isSearchOnFocus, setSearchOnFocus] = useState(false);

  const isSearchPage = pathname === SEARCH;
  const isQuickSearch = isDesktop && !isSearchPage && isSearchOnFocus;

  const updateHistory = useCallback(
    (value) => {
      history.push({
        pathname: SEARCH,
        search: `q=${value}`,
      });
    },
    [history]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(setSearchValue, 300), []);

  function handleChange(value) {
    setSearchInputValue(value);
    debounceFn(value);
  }

  const formSubmitHandler = (event) => {
    event.preventDefault();
    // Prevent form submission if search query hasn't changed
    if (searchInputValue === query) return;
    if (searchInputValue.length < MIN_COUNT_CHARACTERS_FOR_SEARCH) return;
    updateHistory(searchInputValue);
  };

  useEffect(() => {
    if (!isSearchPage) return;
    updateHistory(searchQuery);
  }, [searchQuery, isSearchPage, updateHistory]);

  return (
    <form
      role="search"
      onSubmit={formSubmitHandler}
      className={classNames(
        styles.component,
        isQuickSearch && styles.focus,
        className
      )}
      {...getDataTestAttrs(SEARCH_FORM)}
    >
      <SearchInput
        ref={inputRef}
        value={searchInputValue}
        withBackButton={isSearchPage && !isNative}
        onClear={() => setSearchInputValue('')}
        onChange={handleChange}
        onFocus={() => setSearchOnFocus(true)}
        onBlur={() => setTimeout(() => setSearchOnFocus(false), 200)}
        className={classNames(styles.input, isQuickSearch && styles.focus)}
      />
      {isQuickSearch && <QuickSearch query={searchQuery} />}
    </form>
  );
};

export default SearchBar;
